import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, AlertTriangle, CheckCircle2, TrendingUp, Eye } from "lucide-react";
import { useFraudPatterns } from "@/hooks/useVendorIntelligence";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

const PATTERN_LABELS: Record<string, string> = {
  duplicate_invoice: "Duplicate Invoices",
  anomaly: "Amount Anomalies",
  high_risk_invoice: "High-Risk Vendors",
  timing_anomaly: "Timing Patterns",
};

const PATTERN_ICONS: Record<string, typeof AlertTriangle> = {
  duplicate_invoice: AlertTriangle,
  anomaly: TrendingUp,
  high_risk_invoice: Shield,
  timing_anomaly: Eye,
};

export function FraudCorpusWidget() {
  const { data: patterns, isLoading } = useFraudPatterns();

  if (isLoading) {
    return (
      <Card className="p-6 border-border/50">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-muted rounded w-1/2" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </Card>
    );
  }

  if (!patterns || patterns.length === 0) {
    return (
      <Card className="p-6 border-border/50 bg-gradient-to-br from-success/5 to-transparent">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-success" />
          <h3 className="text-lg font-bold text-foreground">Collective Fraud Defense</h3>
        </div>
        <div className="flex items-center gap-3 text-success">
          <CheckCircle2 className="h-8 w-8" />
          <div>
            <p className="font-medium">No active threats detected</p>
            <p className="text-sm text-muted-foreground">
              Our network is continuously learning from {0} organizations
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Group patterns by type
  const patternsByType = patterns.reduce((acc, p) => {
    if (!acc[p.pattern_type]) {
      acc[p.pattern_type] = [];
    }
    acc[p.pattern_type].push(p);
    return acc;
  }, {} as Record<string, typeof patterns>);

  const totalDetections = patterns.reduce((a, p) => a + p.detection_count, 0);
  const avgConfidence = patterns.reduce((a, p) => a + p.confidence_score, 0) / patterns.length;

  return (
    <Card className="p-6 border-border/50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-bold text-foreground">Collective Fraud Defense</h3>
        </div>
        <Badge variant="outline" className="text-xs">
          {totalDetections.toLocaleString()} detections
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Your transactions are automatically screened against fraud patterns detected across our network. 
        Higher confidence means more confirmed cases.
      </p>

      <div className="space-y-3">
        {Object.entries(patternsByType).map(([type, typePatterns]) => {
          const Icon = PATTERN_ICONS[type] || AlertTriangle;
          const highConfidence = typePatterns.filter(p => p.confidence_score >= 70);
          const totalAmount = typePatterns.reduce(
            (a, p) => a + (p.amount_range_min + p.amount_range_max) / 2, 
            0
          );

          return (
            <div
              key={type}
              className="p-3 rounded-lg bg-surface border border-border/50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-warning" />
                  <span className="text-sm font-medium text-foreground">
                    {PATTERN_LABELS[type] || type}
                  </span>
                </div>
                <Badge 
                  variant={highConfidence.length > 0 ? "destructive" : "secondary"}
                  className="text-xs"
                >
                  {highConfidence.length} high confidence
                </Badge>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                <div>
                  <span className="block text-foreground font-medium">
                    {typePatterns.length}
                  </span>
                  patterns
                </div>
                <div>
                  <span className="block text-foreground font-medium">
                    {typePatterns.reduce((a, p) => a + p.detection_count, 0)}
                  </span>
                  cases
                </div>
                <div>
                  <span className="block text-foreground font-medium">
                    {formatCurrency(typePatterns[0]?.amount_range_min || 0)} - {formatCurrency(typePatterns[0]?.amount_range_max || 0)}
                  </span>
                  typical range
                </div>
              </div>

              {/* Confidence meter */}
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Avg confidence</span>
                  <span className="text-foreground">
                    {(typePatterns.reduce((a, p) => a + p.confidence_score, 0) / typePatterns.length).toFixed(0)}%
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      avgConfidence >= 70 ? "bg-destructive" : 
                      avgConfidence >= 50 ? "bg-warning" : "bg-muted-foreground"
                    }`}
                    style={{ 
                      width: `${typePatterns.reduce((a, p) => a + p.confidence_score, 0) / typePatterns.length}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-success" />
        All your uploads are automatically screened against these patterns
      </div>
    </Card>
  );
}
